import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowSquareOut, DownloadSimple } from '@phosphor-icons/react';
import AlertDialog from './AlertDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tailwind-styled markdown renderer. Restricts what tags get reformatted so the
// final document looks like the rest of the app (dark theme, gold accents).
const md = {
  h1: (p) => <h1 className="text-3xl font-bold font-heading text-gold mt-8 mb-4 first:mt-0" {...p} />,
  h2: (p) => <h2 className="text-2xl font-bold font-heading text-text-primary mt-8 mb-3 pt-4 border-t border-border-color" {...p} />,
  h3: (p) => <h3 className="text-lg font-semibold text-gold mt-5 mb-2" {...p} />,
  p:  (p) => <p className="text-sm text-text-primary leading-relaxed mb-3" {...p} />,
  ul: (p) => <ul className="space-y-1.5 ml-1 mb-4" {...p} />,
  ol: (p) => <ol className="list-decimal ml-5 space-y-1.5 mb-4 text-sm text-text-primary" {...p} />,
  li: ({ children, ...rest }) => {
    const arr = React.Children.toArray(children);
    const first = arr[0];
    const isTask = React.isValidElement(first) && first.props?.type === 'checkbox';
    if (isTask) {
      return (
        <li className="flex items-start gap-2 text-sm text-text-primary leading-relaxed" {...rest}>
          <span className="mt-1 shrink-0 inline-block w-3.5 h-3.5 rounded border border-border-color bg-black/40" />
          <span>{arr.slice(1)}</span>
        </li>
      );
    }
    return <li className="text-sm text-text-primary leading-relaxed list-disc ml-5" {...rest}>{children}</li>;
  },
  strong: (p) => <strong className="font-semibold text-gold" {...p} />,
  em: (p) => <em className="italic text-text-secondary" {...p} />,
  code: ({ inline, children, ...rest }) => (
    inline
      ? <code className="px-1.5 py-0.5 rounded bg-black/50 text-gold font-mono text-xs" {...rest}>{children}</code>
      : <code className="block p-3 rounded-lg bg-black/60 text-gold font-mono text-xs whitespace-pre-wrap" {...rest}>{children}</code>
  ),
  blockquote: (p) => <blockquote className="border-l-2 border-gold/50 pl-3 italic text-text-secondary text-sm my-3" {...p} />,
  hr: () => <hr className="my-6 border-border-color" />,
  table: (p) => <div className="my-3 overflow-x-auto"><table className="w-full text-sm border border-border-color" {...p} /></div>,
  thead: (p) => <thead className="bg-black/40" {...p} />,
  tr: (p) => <tr className="border-b border-border-color last:border-0" {...p} />,
  th: (p) => <th className="px-3 py-2 text-left text-xs font-semibold text-gold uppercase tracking-wide" {...p} />,
  td: (p) => <td className="px-3 py-2 text-text-primary" {...p} />,
  a: (p) => <a className="text-gold underline hover:text-gold-hover" target="_blank" rel="noreferrer" {...p} />,
};

/**
 * Renders a server-side markdown document with Print/Save-as-PDF + Download
 * .md buttons. Use a unique `printableId` per page when more than one
 * MarkdownDocPage instance may be in the DOM (currently never, but cheap to be
 * safe).
 */
export const MarkdownDocPage = ({
  apiPath,
  title,
  subtitle,
  icon,
  downloadFilename,
  printTitle,
  testIdPrefix,
  printableId = 'markdown-doc-printable',
}) => {
  const [body, setBody] = useState('');
  const [err, setErr] = useState(null);
  const [popupNotice, setPopupNotice] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}${apiPath}`, { responseType: 'text', transformResponse: [(x) => x] })
      .then((res) => { if (!cancelled) setBody(res.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || e.message || 'Failed to load'); });
    return () => { cancelled = true; };
  }, [apiPath]);

  const handleDownload = () => {
    const blob = new Blob([body], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadFilename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const popup = window.open('', `${testIdPrefix}-print`, 'width=900,height=1000');
    if (!popup) {
      setPopupNotice(true);
      return;
    }
    const doc = popup.document;
    doc.documentElement.replaceChildren();
    const head = doc.createElement('head');
    const meta = doc.createElement('meta'); meta.setAttribute('charset', 'utf-8'); head.appendChild(meta);
    const titleEl = doc.createElement('title'); titleEl.textContent = printTitle; head.appendChild(titleEl);
    const style = doc.createElement('style');
    style.textContent = `
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        color: #111; background: #fff;
        padding: 1.5cm; line-height: 1.5; font-size: 10pt;
      }
      h1 { font-size: 20pt; font-weight: 700; margin: 16pt 0 6pt; border-bottom: 1px solid #999; padding-bottom: 4pt; }
      h2 { font-size: 14pt; font-weight: 700; margin: 14pt 0 6pt; }
      h3 { font-size: 11pt; font-weight: 700; margin: 10pt 0 4pt; color: #444; }
      p { margin: 0 0 6pt; }
      ul, ol { margin: 0 0 8pt 0; padding-left: 18pt; }
      li { margin-bottom: 3pt; }
      .task { list-style: none; margin-left: 0; padding-left: 0; }
      .task li { padding-left: 18pt; position: relative; }
      .task li::before { content: '☐'; position: absolute; left: 0; font-size: 11pt; }
      strong { font-weight: 700; }
      em { color: #555; font-style: italic; }
      code { background: #eee; padding: 1pt 3pt; border-radius: 2pt; font-family: ui-monospace, Menlo, monospace; font-size: 9pt; }
      blockquote { border-left: 3px solid #999; padding-left: 8pt; color: #555; margin: 6pt 0; font-style: italic; }
      hr { border: 0; border-top: 1px solid #ccc; margin: 12pt 0; }
      table { width: 100%; border-collapse: collapse; margin: 6pt 0; }
      th, td { border: 1px solid #000; padding: 4pt 6pt; text-align: left; vertical-align: top; }
      thead th { background: #e5e5e5; }
      .toolbar { position: fixed; top: 10px; right: 10px; background: #111; color: #fff; padding: 6px 10px; border-radius: 6px; font-size: 11px; display: flex; gap: 6px; }
      .toolbar button { background: #fff; color: #000; border: 0; padding: 3px 8px; border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 11px; }
      @media print { .toolbar { display: none !important; } body { padding: 1cm; } }
    `;
    head.appendChild(style);

    const body2 = doc.createElement('body');
    const toolbar = doc.createElement('div'); toolbar.className = 'toolbar';
    toolbar.appendChild(doc.createTextNode('Ready to print '));
    const printBtn = doc.createElement('button'); printBtn.textContent = 'Print again';
    printBtn.addEventListener('click', () => popup.print());
    toolbar.appendChild(printBtn);
    const closeBtn = doc.createElement('button'); closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => popup.close());
    toolbar.appendChild(closeBtn);
    body2.appendChild(toolbar);

    const source = document.getElementById(printableId);
    if (source) {
      const clone = source.cloneNode(true);
      clone.querySelectorAll('[class]').forEach((el) => el.removeAttribute('class'));
      clone.querySelectorAll('[style]').forEach((el) => el.removeAttribute('style'));
      body2.appendChild(doc.importNode(clone, true));
    }

    doc.documentElement.appendChild(head);
    doc.documentElement.appendChild(body2);
    const fire = () => {
      try { popup.focus(); } catch (_) { /* intentionally ignored */ }
      popup.print();
    };
    if (doc.readyState === 'complete') setTimeout(fire, 150);
    else popup.addEventListener('load', () => setTimeout(fire, 150));
  };

  return (
    <div className="flex-1 p-8" data-testid={`${testIdPrefix}-page`}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-2 flex items-center gap-3" data-testid={`${testIdPrefix}-title`}>
              {icon}
              {title}
            </h1>
            {subtitle && <p className="text-text-secondary text-sm">{subtitle}</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={handlePrint}
              disabled={!body}
              data-testid={`${testIdPrefix}-print`}
              className="flex items-center gap-1 px-3 py-1.5 border border-gold text-gold text-sm rounded-lg hover:bg-gold hover:text-black disabled:opacity-50"
            >
              <ArrowSquareOut size={16} weight="bold" /> Print / PDF
            </button>
            <button
              onClick={handleDownload}
              disabled={!body}
              data-testid={`${testIdPrefix}-download`}
              className="flex items-center gap-1 px-3 py-1.5 border border-border-color text-text-primary text-sm rounded-lg hover:bg-surface-hover disabled:opacity-50"
            >
              <DownloadSimple size={16} weight="bold" /> Download .md
            </button>
          </div>
        </div>

        {err && (
          <div className="text-sm px-3 py-2 rounded text-danger bg-danger/10 border border-danger/30" data-testid={`${testIdPrefix}-error`}>
            {err}
          </div>
        )}
        {!err && !body && (
          <p className="text-text-secondary text-sm" data-testid={`${testIdPrefix}-loading`}>Loading…</p>
        )}
        {body && (
          <div id={printableId} className="bg-surface border border-border-color rounded-xl p-6" data-testid={`${testIdPrefix}-content`}>
            <ReactMarkdown components={md} remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
          </div>
        )}
      </div>
      <AlertDialog
        open={popupNotice}
        title="Popups blocked"
        message="Please allow popups to print or save as PDF."
        variant="warning"
        onClose={() => setPopupNotice(false)}
        testIdPrefix={`${testIdPrefix}-popup-blocked-alert`}
      />
    </div>
  );
};

export default MarkdownDocPage;
